import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { ButtonSkeleton } from "@circleco/compass/components/ButtonSkeleton";
import { Modal } from "@circleco/compass/components/Modal";
import { Skeleton } from "@circleco/compass/components/Skeleton";
import { TypographySkeleton } from "@circleco/compass/components/TypographySkeleton";

import { t } from "@/i18n-js/instance";
import { PlanTrialCardSkeleton } from "@/react/components/SettingsApp/Plan/PlanTrialOptInModal/PlanTrialOptInModalSkeleton/PlanTrialCardSkeleton";

interface PlanTrialOptInModalSkeletonProps {
  onCancel?: () => void;
  title?: string;
}

const FEATURES = [
  "community_ai",
  "workflows_apis",
  "custom_fields",
  "built_for_scale",
];

export const PlanTrialOptInModalSkeleton = ({
  onCancel,
  title,
}: PlanTrialOptInModalSkeletonProps) => {
  const modal = useModal();

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onCancel?.();
      void modal.hide();
      modal.remove();
    }
  };

  return (
    <Modal.Root open={modal.visible} onOpenChange={handleOpenChange} size="xl">
      <Modal.Content>
        <Modal.Header
          title={title ?? t("settings.billing.plans.trial_opt_in_modal.title")}
          showCloseButton
        />
        <Modal.Body>
          <div className="flex h-full" aria-busy="true">
            <div className="flex w-full flex-col pb-6 pr-4 lg:w-1/2 lg:pb-0">
              <TypographySkeleton
                variant="body-md"
                lines={3}
                className="mb-6 w-full gap-0.5"
              />
              <div className="space-y-4">
                {FEATURES.map(feature => (
                  <div key={feature} className="flex gap-3">
                    <Skeleton
                      shape="rounded"
                      className="mt-1 size-5 shrink-0"
                    />
                    <div className="flex-1 space-y-1">
                      <TypographySkeleton
                        variant="label-md"
                        className="w-2/3"
                      />
                      <TypographySkeleton
                        variant="body-sm"
                        lines={2}
                        className="w-full gap-0.5"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-auto pt-6">
                <div className="my-4 lg:hidden">
                  <PlanTrialCardSkeleton />
                </div>
                <ButtonSkeleton size="lg" fullWidth />
              </div>
            </div>
            <div className="hidden w-1/2 pl-4 lg:block">
              <div className="border-primary overflow-hidden rounded-lg border">
                <Skeleton className="aspect-[125/108] w-full" />
                <PlanTrialCardSkeleton isAttached />
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
